import * as React from "react";

import { Badge, Button, IconButton, Input, useStableId } from "Components/ui";
import { Plus, X } from "Components/ui/icons";
import { describeOffsetDays } from "utils/format";
import { MAX_OFFSETS } from "../rules";

interface ReminderOffsetsEditorProps {
  offsets: number[];
  onChange: (next: number[]) => void;
  disabled: boolean;
}

/** Editable list of reminder days relative to the due date (-7 = a week before, 1 = a day after). */
const ReminderOffsetsEditor: React.FC<ReminderOffsetsEditorProps> = ({
  offsets,
  onChange,
  disabled,
}) => {
  const [draft, setDraft] = React.useState<string>("");
  const [error, setError] = React.useState<string | null>(null);
  const errorId = `${useStableId("reminder-offset")}-error`;
  const sorted = offsets.slice().sort((a, b) => a - b);

  const add = () => {
    const text = draft.trim().replace("−", "-");
    if (!/^[-+]?\d+$/.test(text)) {
      setError("Type a whole number of days, e.g. -7 for a week before or 1 for a day after.");
      return;
    }
    const value = Number(text);
    if (value < -60 || value > 60) {
      setError("Use -60 to 60 days.");
      return;
    }
    if (offsets.includes(value)) {
      setError("That day is already in the list.");
      return;
    }
    if (offsets.length >= MAX_OFFSETS) {
      setError(`Use at most ${MAX_OFFSETS} reminders.`);
      return;
    }
    onChange([...offsets, value].sort((a, b) => a - b));
    setDraft("");
    setError(null);
  };

  return (
    <div>
      <div className="flex flex-wrap gap-2">
        {sorted.length === 0 && (
          <span className="text-sm text-muted-foreground">No reminder days.</span>
        )}
        {sorted.map((offset) => (
          <Badge key={offset} tone="primary" size="md" className={disabled ? undefined : "pr-1"}>
            <span className="font-semibold tabular-nums">{offset > 0 ? `+${offset}` : offset}</span>
            <span className="font-normal">{describeOffsetDays(offset)}</span>
            {!disabled && (
              <IconButton
                label={`Remove reminder ${describeOffsetDays(offset)}`}
                icon={<X aria-hidden="true" />}
                size="sm"
                onClick={() => onChange(offsets.filter((value) => value !== offset))}
                className="h-5 w-5 rounded-full [&_svg]:h-3 [&_svg]:w-3"
              />
            )}
          </Badge>
        ))}
      </div>
      {!disabled && (
        <div className="mt-2 flex items-center gap-2">
          <Input
            type="text"
            inputMode="numeric"
            value={draft}
            placeholder="e.g. -3"
            aria-label="Reminder day relative to the due date"
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? errorId : undefined}
            onChange={(event) => {
              setDraft(event.target.value);
              setError(null);
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                add();
              }
            }}
            wrapperClassName="w-28"
            className="tabular-nums"
          />
          <Button
            variant="secondary"
            size="sm"
            onClick={add}
            leftIcon={<Plus aria-hidden="true" />}
          >
            Add day
          </Button>
        </div>
      )}
      {error && (
        <p id={errorId} className="mt-1 text-xs text-danger-text">
          {error}
        </p>
      )}
    </div>
  );
};

export default ReminderOffsetsEditor;
